import { GetStaticPaths, GetStaticProps } from "next";
import { FunctionComponent } from "react";
import {
  SuperHero,
  BrewGuidesCollection,
  DropdownOption,
} from "@bluebottlecoffee/design-system/components";
import { CanonicalHead } from "../../../../components/CanonicalHead";
import { globalModulesProjection } from "../../../../lib/sanity/global-modules";
import sanityClient from "../../../../lib/sanity-client";
import renderModule from "../../../../lib/render-module";
import { toSuperHeroProps } from "../../../../lib/transformers/super-hero";
import { LayoutWrapper } from "../../../../components/LayoutWrapper";
import {
  getDefaultStaticPaths,
  getDefaultStaticProps,
} from "../../../../lib/utils";
import {
  BrewGuide,
  BrewGuideCard,
  BrewGuideSortBy,
  BrewGuidesPage as BrewGuidesPageSchema,
} from "../../../../lib/sanity-schema";
import { BaseProps } from "../../../../lib/sanity/shared";
import {
  filters,
  getLocalizedString,
  getSlug,
  keyof,
} from "../../../../lib/sanity/utils";
import { toBrewGuideCardProps } from "../../../../lib/transformers/brew-guide-card";
import { resolveTabbedCollections } from "../../../../lib/resolve-tabbed-collections";

interface PageData extends BaseProps {
  brewGuidesPage: BrewGuidesPageSchema;
  brewGuidesSortBy: DropdownOption[];
}

const brewGuideSortBy = (lang: string) => `{
    "display": ${getLocalizedString<BrewGuideSortBy>("display", lang)},
    "value": ${getSlug<BrewGuideSortBy>("value")},
  }`;

export const getStaticProps: GetStaticProps<PageData> = async (context) => {
  const { props: defaultProps } = await getDefaultStaticProps(context);
  const { lang, region } = defaultProps;

  const brewGuidesPage = await sanityClient().fetch<BrewGuidesPageSchema>(`*[
    ${filters.whereType("brewGuidesPage")} &&
    ${filters.excludeDrafts}
    ][0]{...,
      ${keyof<BrewGuidesPageSchema>("brewGuides")}[]->{
        ...,
        ${keyof<BrewGuide>("card")}[]{
          ...,
          ${keyof<BrewGuideCard>("sortBy")}[]->${brewGuideSortBy(lang)}
        },
      }, 
      modules[]{
        ...,
        ${globalModulesProjection(lang)},
      }
    }`);

  const brewGuidesSortBy = await sanityClient().fetch(`*[
    ${filters.whereType("brewGuideSortBy")} &&
    ${filters.excludeDrafts}
    ]${brewGuideSortBy(lang)}`);

  await resolveTabbedCollections(brewGuidesPage?.modules, { lang, region });

  return {
    props: {
      brewGuidesPage,
      brewGuidesSortBy,
      ...defaultProps,
    },
  };
};

export const getStaticPaths: GetStaticPaths = getDefaultStaticPaths;

function validate(data: BrewGuidesPageSchema) {
  if (!data.title) {
    throw Error("Brew Guides Page page must have a title.");
  }
}

const BrewGuidesPage: FunctionComponent<PageData> = ({
  brewGuidesPage,
  brewGuidesSortBy,
  ...layoutWrapperProps
}) => {
  validate(brewGuidesPage);

  const { region, lang, appPlayLinks, aria } = layoutWrapperProps;

  const brewGuideCards = brewGuidesPage.brewGuides.map((brewGuide) =>
    toBrewGuideCardProps(brewGuide as unknown as BrewGuide, lang, region),
  );

  return (
    <>
      <CanonicalHead region={region} lang={lang} slug="brew-guides" />
      <LayoutWrapper
        {...{
          ...layoutWrapperProps,
          pageTitle: brewGuidesPage.title[lang],
          metaTags: brewGuidesPage.metaTags,
        }}
        isTransparent
      >
        {brewGuidesPage.hero?.length > 0 && (
          <SuperHero
            {...toSuperHeroProps(brewGuidesPage.hero, aria, region, lang)}
          />
        )}

        <BrewGuidesCollection
          brewGuideCards={brewGuideCards}
          heading={brewGuidesPage.heading[lang]}
          toggleLabels={{
            brewGuide: brewGuidesPage.toggleLabels.brewGuides[lang],
            recipes: brewGuidesPage.toggleLabels.recipes[lang],
          }}
          sortByLabel={brewGuidesPage.sortBy[lang]}
          sortByOptions={brewGuidesSortBy}
        />

        {brewGuidesPage.modules?.length > 0 &&
          brewGuidesPage.modules.map((module) =>
            renderModule({
              module,
              region,
              lang,
              appPlayLinks,
              aria,
            }),
          )}
      </LayoutWrapper>
    </>
  );
};

export default BrewGuidesPage;
